import { InjectedConnector } from 'starknetkit/injected';
import { ArgentMobileConnector, isInArgentMobileAppBrowser } from 'starknetkit/argentMobile';
import { BraavosMobileBaseConnector, isInBraavosMobileAppBrowser } from 'starknetkit/braavosMobile';
import { WebWalletConnector } from 'starknetkit/webwallet';
import { jsonRpcProvider } from '@starknet-react/core';
import { Connector } from 'starknetkit';
import { resilientFetch } from '../utils/resilientFetch';

type MutableConnector = Connector & { id?: string; name?: string };

const isIos =
  typeof window !== 'undefined'
    ? /iP(ad|od|hone)|MacIntel/i.test(window.navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    : false;
const isMobileDevice = () => {
  // Primary method: User Agent + Touch support check
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|windows phone|MacIntel/.test(userAgent);
  const hasTouchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Backup method: Screen size
  const isSmallScreen = window.innerWidth <= 768;

  // Combine checks: Must match user agent AND (touch support OR small screen)
  return isMobileUA && (hasTouchSupport || isSmallScreen);
};

const argentMobileConnector = ArgentMobileConnector.init({
  options: {
    dappName: 'Nostra',
    url: 'https://app.nostra.finance',
  },
  inAppBrowserOptions: {},
});
const argentXConnector = new InjectedConnector({ options: { id: 'argentX' } });
const braavosConnector = new InjectedConnector({ options: { id: 'braavos' } });
const metamaskConnector = new InjectedConnector({ options: { id: 'metamask' } });
const okxConnector = new InjectedConnector({ options: { id: 'okxwallet' } });
const keplrConnector = new InjectedConnector({ options: { id: 'keplr' } });
const braavosMobileConnector = new BraavosMobileBaseConnector();
const argentEmailConnector = new WebWalletConnector({ url: 'https://web.argent.xyz' });

const baseStarknetConnectors: Connector[] = isInArgentMobileAppBrowser()
  ? [argentMobileConnector]
  : isInBraavosMobileAppBrowser()
  ? [braavosConnector]
  : isIos
  ? [argentMobileConnector, braavosMobileConnector, argentEmailConnector]
  : isMobileDevice()
  ? [argentMobileConnector, braavosMobileConnector, okxConnector, argentEmailConnector]
  : [
      argentXConnector,
      braavosConnector,
      metamaskConnector,
      okxConnector,
      keplrConnector,
      argentEmailConnector,
      argentMobileConnector,
    ];

const shouldHideReadyMobileConnector = (connector: Connector): boolean => {
  const { id = '', name = '' } = connector as MutableConnector;
  const normalizedId = id.toLowerCase();
  const normalizedName = name.toLowerCase();
  const isReadyConnector = normalizedId.includes('ready') || normalizedName.includes('ready');
  const isMobileConnector = normalizedId.includes('mobile') || normalizedName.includes('mobile');

  return isReadyConnector && isMobileConnector;
};

const renameReadyWalletConnector = (connector: Connector): Connector => {
  const mutableConnector = connector as MutableConnector;
  const normalizedId = mutableConnector.id?.toLowerCase() ?? '';
  const normalizedName = mutableConnector.name?.toLowerCase().trim() ?? '';
  const isReadyConnector =
    normalizedId.includes('ready') ||
    normalizedName === 'ready wallet' ||
    normalizedName === 'ready';

  if (isReadyConnector && !normalizedName.includes('mobile')) {
    mutableConnector.name = 'Ready X';
  }

  return connector;
};

export const starknetConnectors: Connector[] = baseStarknetConnectors
  .filter(connector => !shouldHideReadyMobileConnector(connector))
  .map(renameReadyWalletConnector);

export const starknetProvider = jsonRpcProvider({
  rpc: () => ({
    nodeUrl: import.meta.env.VITE_APP_RPC_URL!,
    retries: 3,
    baseFetch: resilientFetch,
  }),
});
